import { Button, Menu, MenuItem } from '@material-ui/core';
import { useState, MouseEvent, ReactNode } from 'react';
import { useJoinEvent } from '../../hooks/useJoinEvent';
import { useAnalytics } from '@backstage/frontend-plugin-api';

type ButtonAppearance = {
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
};

const defaultButtonAppearance: ButtonAppearance = {
  size: 'small',
  variant: 'outlined',
  color: 'default',
};

const primaryButtonAppearance: ButtonAppearance = {
  size: 'small',
  variant: 'contained',
  color: 'primary',
};

interface InviteButtonProps {
  eventId: string;
  eventSeriesId?: string;
  isPrimaryButton: boolean;
  disabled?: boolean;
  label?: string;
  endIcon?: ReactNode;
}
export const InviteButton = ({
  eventId,
  eventSeriesId,
  isPrimaryButton,
  disabled = false,
  label = 'Invite me',
  endIcon,
}: InviteButtonProps) => {
  const [menu, setMenu] = useState<null | HTMLElement>(null);

  const analytics = useAnalytics();
  const { joinEvent, isJoining } = useJoinEvent();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (eventSeriesId) {
      setMenu(event.currentTarget);
    } else {
      analytics.captureEvent('click', 'invite_single_event');
      joinEvent(eventId);
    }
  };

  const handleClose = () => {
    setMenu(null);
  };

  const handleInviteSingle = () => {
    joinEvent(eventId);
    analytics.captureEvent('click', 'invite_single_event');
    handleClose();
  };

  const handleInviteSeries = () => {
    joinEvent(eventSeriesId!);
    analytics.captureEvent('click', 'invite_recurring_event');
    handleClose();
  };

  const getButtonText = (): string => {
    const whenUnpressed: string = eventSeriesId ? `${label}...` : label;
    const whenPressed = 'Inviting...';
    return isJoining ? whenPressed : whenUnpressed;
  };

  const buttonAppearance = isPrimaryButton ? primaryButtonAppearance : defaultButtonAppearance;
  return (
    <>
      <Button
        {...buttonAppearance}
        onClick={handleClick}
        disabled={disabled || isJoining}
        endIcon={isJoining ? undefined : endIcon}
      >
        {getButtonText()}
      </Button>
      {eventSeriesId && (
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={handleClose}>
          <MenuItem onClick={handleInviteSingle}>This event only</MenuItem>
          <MenuItem onClick={handleInviteSeries}>All events in series</MenuItem>
        </Menu>
      )}
    </>
  );
};
